import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, CheckCircle2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { salesApi } from '../salesApi';
import { useT, fmtDate, localeOf } from '../salesI18n';

const fmtMoney = (v, lang) => new Intl.NumberFormat(localeOf(lang), { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0));

const Stat = ({ label, value, tone, testId }) => (
  <div className={`rounded-2xl border p-3 ${tone}`} data-testid={testId}>
    <p className="text-[11px] font-semibold text-slate-500">{label}</p>
    <p className="text-lg sm:text-xl font-extrabold tracking-tight text-slate-900 tabular-nums mt-0.5">{value}</p>
  </div>
);

export const CommissionCard = ({ isManager = false }) => {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { salesApi.get('/commissions').then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);
  if (!data || data.error) return null;

  const m = data.month_summary || {};
  const a = data.all_time || {};
  const items = data.items || [];
  const raw = fmtDate(`${data.month}-15`, lang, { month: 'long', year: 'numeric' });
  const monthLabel = raw.charAt(0).toUpperCase() + raw.slice(1);

  return (
    <section className="bg-card rounded-xl border border-border p-4 sm:p-6 shadow-sm" data-testid="commission-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-10 w-10 rounded-2xl bg-teal-600 text-white flex items-center justify-center shrink-0 shadow-sm"><Wallet className="h-5 w-5" /></span>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-base sm:text-lg text-slate-900">{isManager ? t.teamCommissions : t.myCommissions}</h2>
            <p className="text-xs text-slate-500">{monthLabel} · {t.rateLabel} <b className="text-teal-700" data-testid="commission-rate">{data.rate_percent}%</b></p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Stat label={t.thisMonth} value={fmtMoney(m.total, lang)} tone="bg-white/70 border-white/90" testId="commission-month-total" />
        <Stat label={t.toReceive} value={fmtMoney(a.pending_total, lang)} tone="bg-amber-50/80 border-amber-200/80" testId="commission-pending-total" />
        <Stat label={t.alreadyPaid} value={fmtMoney(a.paid_total, lang)} tone="bg-teal-50/80 border-teal-200/80" testId="commission-paid-total" />
      </div>

      {items.length > 0 ? (
        <div className="mt-4">
          <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-xs font-semibold text-teal-700 hover:text-teal-900 py-1" data-testid="commission-toggle-list">
            <span>{t.commissionSales} ({a.count}) · {fmtMoney(a.sales_total, lang)}</span>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {open && (
            <ul className="mt-2 divide-y divide-slate-200/60" data-testid="commission-list">
              {items.map((c) => (
                <li key={c.id} className="py-2.5 flex items-center gap-3" data-testid={`commission-item-${c.id}`}>
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${c.status === 'paid' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`}>{c.status === 'paid' ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}</span>
                  <div className="min-w-0 flex-1">
                    <Link to={`/leads/${c.lead_id}?tab=proposal`} className="text-sm font-semibold text-slate-900 hover:text-teal-700 truncate block">{c.lead_name}</Link>
                    <p className="text-[11px] text-slate-500 truncate">{isManager && c.seller_name ? `${c.seller_name} · ` : ''}{c.contract_number} · {fmtDate(c.sale_date, lang)} · {t.sale} {fmtMoney(c.sale_total, lang)} · {c.rate_percent}%</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-extrabold text-slate-900 tabular-nums">{fmtMoney(c.amount, lang)}</p>
                    <p className={`text-[10px] font-semibold ${c.status === 'paid' ? 'text-teal-700' : 'text-amber-700'}`} data-testid={`commission-item-status-${c.id}`}>{c.status === 'paid' ? `${t.paidLabel} ${fmtDate(c.paid_at, lang, { day: '2-digit', month: 'short' })}` : t.pendingLabel}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <p className="mt-4 text-xs text-slate-500" data-testid="commission-empty">{t.noCommissions}</p>
      )}
    </section>
  );
};
